import { LogFirebase } from "./firestore";
import navigatorService from "./navigatorService";
import { getRoom } from "../components/Twilio/Room/roomService";
import { getTracksByType } from "../components/Twilio/twilioCallUtility";

class AskAIService {
    /**
     * Capture screenshot from the current video stream
     * @returns {string|null} - Base64 encoded image or null if failed
     */
    captureScreenshot() {
        try {
            const video = document.querySelector("#expandedVideo video");

            if (!video || !video.videoWidth || !video.videoHeight) {
                console.error("Video element not found or not ready");
                LogFirebase("AskAI", "captureScreenshot_no_video", "");
                return null;
            }

            const width = video.videoWidth;
            const height = video.videoHeight;

            LogFirebase("AskAI", "captureScreenshot_dimensions", { width, height });

            // Try to use ImageCapture API if supported
            const room = getRoom();
            const localVideoTracks = getTracksByType(room?.localParticipant, "video");

            if (navigatorService.isImageCaptureSupported(localVideoTracks)) {
                return this.captureUsingImageCaptureAPI(video, width, height, localVideoTracks);
            } else {
                return this.captureUsingCanvas(video, width, height);
            }
        } catch (error) {
            console.error("Error capturing screenshot:", error);
            LogFirebase("AskAI", "captureScreenshot_error", error?.message);
            return null;
        }
    }

    /**
     * Capture screenshot using ImageCapture API (for supported browsers)
     * @param {HTMLVideoElement} video
     * @param {number} width
     * @param {number} height
     * @param {Array} localVideoTracks
     * @returns {string|null}
     */
    captureUsingImageCaptureAPI(video, width, height, localVideoTracks) {
        try {
            const objStream = new MediaStream();
            objStream.addTrack(localVideoTracks[0].mediaStreamTrack);
            const [track] = objStream.getVideoTracks();
            const imageCapture = new ImageCapture(track);

            // Note: ImageCapture.grabFrame() returns a promise, but we'll use synchronous canvas method
            // for consistency with the existing codebase pattern
            return this.captureUsingCanvas(video, width, height);
        } catch (error) {
            console.error("Error using ImageCapture API:", error);
            LogFirebase("AskAI", "captureUsingImageCaptureAPI_error", error?.message);
            return this.captureUsingCanvas(video, width, height);
        }
    }

    /**
     * Capture screenshot using Canvas (fallback method)
     * @param {HTMLVideoElement} video
     * @param {number} width
     * @param {number} height
     * @returns {string|null}
     */
    captureUsingCanvas(video, width, height) {
        try {
            // Create a temporary canvas
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext("2d");
            context.drawImage(video, 0, 0, width, height);

            // Convert to base64
            const screenshot = canvas.toDataURL("image/jpeg", 0.8);

            LogFirebase("AskAI", "captureUsingCanvas_success", "");

            return screenshot;
        } catch (error) {
            console.error("Error using canvas capture:", error);
            LogFirebase("AskAI", "captureUsingCanvas_error", error?.message);
            return null;
        }
    }

    /**
     * Scroll chat to bottom with smooth behavior
     * Uses multiple techniques to ensure reliable scrolling across all scenarios
     */
    scrollToBottom() {
        const scrollContainer = document.querySelector(".askai-sidebar-body");
        if (!scrollContainer) return;

        // Method 1: Immediate scroll (for instant feedback)
        scrollContainer.scrollTop = scrollContainer.scrollHeight;

        // Method 2: requestAnimationFrame for post-render scroll
        requestAnimationFrame(() => {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;

            // Method 3: Delayed scroll for dynamic content (images, formatting)
            setTimeout(() => {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }, 100);

            // Method 4: Additional delay for mobile keyboards and slower devices
            setTimeout(() => {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }, 300);
        });
    }
}

export default new AskAIService();
